import { observable, action, computed, toJS } from 'mobx'
import agent from '@/Agent'
import PrePlayStore from '@/stores/PrePlayStore'
import config from '@/Agent/config'
import _ from 'lodash'
import { uniqueId, fIdx } from '../utils'
import { Play_Stats } from '../Agent/GoogleAnalytics'

const isPlayDataDifferent = (playA, playB) => {
  if (!playA || !playB) return true
  const idA = playA.id || playA.gamePlayId
  const idB = playB.id || playB.gamePlayId
  if (idA != idB) return true
  if (playA.stars !== playB.stars) return true
  if (playA.sponsorId !== playB.sponsorId) return true
  if (playA.participantId !== playB.participantId) return true

  // Compare playTitle
  const titleA =
    playA.playTitle && typeof playA.playTitle === 'object'
      ? playA.playTitle.value
      : playA.playTitle
  const titleB =
    playB.playTitle && typeof playB.playTitle === 'object'
      ? playB.playTitle.value
      : playB.playTitle
  if (titleA !== titleB) return true

  // Compare choices
  const choicesA = playA.choices || []
  const choicesB = playB.choices || []
  if (choicesA.length !== choicesB.length) return true
  for (let i = 0; i < choicesA.length; i++) {
    if (choicesA[i].value !== choicesB[i].value) return true
  }

  // Compare pmetadata
  const metaA = playA.pmetadata || {}
  const metaB = playB.pmetadata || {}

  const getTimestamp = val => {
    if (!val) return 0
    if (val instanceof Date) return val.getTime()
    return new Date(val).getTime()
  }

  if (
    metaA.isPlayPaused !== metaB.isPlayPaused ||
    metaA.pausedTime !== metaB.pausedTime ||
    getTimestamp(metaA.playStartTime) !== getTimestamp(metaB.playStartTime) ||
    metaA.timerInput !== metaB.timerInput ||
    metaA.brandMode !== metaB.brandMode
  ) {
    return true
  }

  return false
}

class GameStore {
  currentGameEvent = 'fbnfl'

  @action
  setCurrentGameEvent(val) {
    this.currentGameEvent = val
  }

  gameEvents = ['fbnfl', 'bbnba']

  @observable
  isLoading = false

  @observable
  isError = false
  @action
  setError(val) {
    this.isError = val
  }

  @observable
  isAvailable = false

  gameId = null

  @observable
  progress = 'active'

  @observable
  prePicks = []

  @observable
  prePickUpdateCounter = 0

  @observable
  isGAEnabled = false

  @action setGAEnabled() {
    this.isGAEnabled = true
  }

  @action
  incrementPrePickUpdateCounter() {
    this.prePickUpdateCounter = this.prePickUpdateCounter + 1
  }

  dateEndSession = null
  progressStates = null

  participants = []
  preset = []
  baseOptions = []
  baseDefaults = []
  timePeriods = []
  interruptPlays = []
  messagePlays = []
  venue = null

  recordedPlays = []
  lastSessionTime = 0
  videoPath = null
  isTimeStarted = false
  sponsorPackages = []
  sportTypes = []

  @observable
  lastSequence = 0

  @observable
  HCommLastHeaderSequence = 0

  @observable
  HCommLastPlaySequence = 0

  @observable
  HCommLastWait = 0

  @observable
  automationRestartedServerCallback = false

  @computed
  get sessionIsStarted() {
    return this.progress === 'live' ? true : false
  }

  connectSC(ikey) {
    return agent.GameServer.connectSC(ikey)
  }

  initialGameInfo = null
  @action
  setInitialGameInfo(val) {
    this.initialGameInfo = val
  }

  @observable
  loadingErrorMessage = null

  @action
  setLoadingErrorMessage(msg) {
    this.loadingErrorMessage = msg
  }

  async subscribeToGame(params) {
    this.isLoading = true
    PrePlayStore.resetValues()
    if (params && params.event) {
      this.gameId = params.event
    }
    agent.GameServer.getBrands().then(brands => {
      if (brands && brands.length > 0) this.brandAssets = brands
    })

    try {
      await this.assetsInit()
    } catch (e) {
      console.log('failed to fetch brandapp assets init', e)
    }

    agent.GameServer.subscribeToGame(params, this.gameEvents)
  }

  @observable
  hasReset = false
  setHasReset(val) {
    PrePlayStore.resetValues()
    this.hasReset = val
  }

  preprocessItem(data, reset_started, skip_sponsor) {
    if (data.multiplierChoices && data.multiplierChoices.length) {
      let [_first_question_index, _choices] = fIdx(
        data.multiplierChoices,
        o => o.id == data.id
      )
      if (_choices) data.choices = _choices.choices
      if (data.choices) data.choices.sort((a, b) => a.sequence - b.sequence)
      if (_first_question_index > 0)
        data.multiplierChoices.unshift(
          data.multiplierChoices.splice(_first_question_index, 1)[0]
        )
    } else {
      data.multiplierChoices = []
    }
    if (!data.choices) data.choices = []

    data.index = data.id
    data.length = 0
    data.lockedOut = false
    data.multiplierItems = []
    data.playInProcess = false
    data.resultConfirmed = false
    data.sessionStarted = this.progress === 'live'
    if (reset_started) data.started = null
    data.playTitle = { id: 1, value: data.playTitle }

    data.team = PrePlayStore.teams.filter(
      o => o.publicId === data.participantId
    )[0]

    data.preset = PrePlayStore.presetItems.filter(
      o =>
        (o.preset || '').toLowerCase() ===
          (data.presetId || '').toLowerCase() &&
        o.type.toLowerCase() === data.type.toLowerCase()
    )[0]
    if (!data.preset) data.preset = {}

    if (!skip_sponsor)
      data.sponsor = this.getSelectedSponsor(data.sponsorId, data)

    for (let i = 0; i < data.multiplierChoices.length; i++) {
      data.multiplierChoices[i].choices.sort((a, b) => a.sequence - b.sequence)
    }
    return data
  }

  setSportTypeInfo(data) {
    console.log('SPORT TYPE INFO: ', data)

    if (data) {
      this.preset = data.preset
      this.baseOptions = data.baseOptions
      if (data.defaults || !this.baseDefaults)
        this.baseDefaults = data.defaults || []
      this.timePeriods = data.timePeriods
      this.interruptPlays = data.interruptionPeriods
      this.messagePlays = data.messagePeriods
      this.gmMesPlays = data.gmMesPeriods || []
      this.mesVidPlays = data.mesVidPeriods || []
      if (data.sportTypes) this.sportTypes = data.sportTypes

      this.extractFromSportTypeOnly()
    }
  }

  setPrepicks(data) {
    this.prePickUpdateCounter = 0
    console.log('PREPICKS: ', data) //tmp
    if (data && data.gameId && this.gameId && data.gameId === this.gameId) {
      this.prePicks = data.prePicks
      this.extractPrePicks().then(function() {})
    }
  }

  @observable
  gmetadata = null

  setInfo(data) {
    this.prePickUpdateCounter = 0
    console.log('INFO: ', data) //tmp

    if (data) {
      this.gameId = data.gameId
      this.progress = data.progress
      this.lastSessionTime = data.lastSessionTime
      this.lastSequence = data.lastSequence
      this.HCommLastHeaderSequence = data.HCommLastHeaderSequence
      this.HCommLastPlaySequence = data.HCommLastPlaySequence
      this.HCommLastWait = data.HCommLastWait
      this.dateEndSession = data.dateEndSession
      this.progressStates = data.progressStates

      this.participants = data.participants
      this.preset = data.preset
      this.baseOptions = data.baseOptions
      this.baseDefaults = data.defaults || []
      this.timePeriods = data.timePeriods
      this.interruptPlays = data.interruptionPeriods
      this.messagePlays = data.messagePeriods
      this.gmMesPlays = data.gmMesPeriods || []
      this.mesVidPlays = data.mesVidPeriods || []
      this.venue = data.venue
      this.prePicks = data.prePicks
      this.videoPath = data.videoPath
      this.isTimeStarted = data.isTimeStarted
      this.recordedPlays = data.recordedPlays
      this.sportTypes = data.sportTypes
      if (data.sportType) {
        this.setSportType(data.sportType)
      }
      this.gmetadata = data.gmetadata
      if (!this.gamemasters) this.setGameMasters()

      //this.sponsorPackages = data.sponsorPackages
      this.automationRestartedServerCallback =
        data.automationRestartedServerCallback || false

      // if (
      //   this.recordedPlays &&
      //   Array.isArray(this.recordedPlays) &&
      //   this.recordedPlays.length < 1
      // ) {
      //   this.recordedPlays = data.recordedPlays
      // }

      // if (this.sponsorPackages &&
      //   Array.isArray(this.sponsorPackages) &&
      //   this.sponsorPackages.length < 1
      // ) {
      //   this.sponsorPackages = data.sponsorPackages
      // }

      // if (this.sportTypes &&
      //   Array.isArray(this.sportTypes) &&
      //   this.sportTypes.length < 1
      // ) {
      //   this.sportTypes = data.sportTypes
      // }

      if (data.isFootageRecorded) {
        if (this.executionType === 'recording') {
          this.setRecordEnded(true)
        }
        this.setSessionMode(0)
      }

      if (data.dateEndSession) {
        PrePlayStore.sessionButtons['start'] = {
          backgroundColor: '#18c5ff',
          color: '#000000',
          text: 'session ended',
          locked: true,
        }
        PrePlayStore.sessionButtons['end'].locked = true
      } else {
        PrePlayStore.sessionButtons['start'].locked = data.progress === 'live'
      }

      PrePlayStore.setCurrentPlayItem(data.currentPlay)
      if (data.unresolvedPlays) {
        //PrePlayStore.setUnresolvedItems(data.unresolvedPlays)
        //PrePlayStore.unresolvedItems.unshift(data.unresolvedPlay)
        this.setPlayUnresolved(data.unresolvedPlays)
      }

      if (data.resolvedPlays) {
        this.setPlayResolved(data.resolvedPlays)
      }

      this.extractPresets()

      agent.GameServer.setHeadless(this.headless)
    } else {
      this.gameId = null
      this.isAvailable = false
      this.isLoading = false
    }
  }

  extractPresets() {
    this.extractTeams()
      .then(next => {
        if (next) {
          return this.assignIdOnPredetermined()
        }
      })
      .then(next => {
        if (next) {
          return this.extractPreDetermined()
        }
      })
      .then(next => {
        if (next) {
          return this.extractBaseOptions()
        }
      })
      /* //tmp to delete
      .then(next => {
        if (next) {
          return this.addNonLivePlayOptions()
        }
      }) */
      .then(next => {
        if (next) {
          return this.extractTimePeriods()
        }
      })
      .then(next => {
        if (next) {
          return this.extractInterruptPlays()
        }
      })
      .then(next => {
        if (next) {
          return this.extractMessagePlays()
        }
      })
      .then(next => {
        if (next) {
          return this.extractGmMesPlays()
        }
      })
      .then(next => {
        if (next) {
          return this.extractMesVidPlays()
        }
      })
      .then(next => {
        if (next) {
          return this.extractSponsors()
        }
      })
      .then(next => {
        if (next) {
          this.extractPrePicks()
        }
      })
      .finally(() => {
        this.isLoading = false
        this.isAvailable = true

        //AUTOMATION
        if (
          this.recordedPlays &&
          Array.isArray(this.recordedPlays) &&
          this.recordedPlays.length > 2 &&
          'recording' === this.executionType
        ) {
          PrePlayStore.sessionButtons['start'] = {
            backgroundColor: '#18c5ff',
            color: '#000000',
            text: 'record ended',
            locked: true,
          }
          PrePlayStore.sessionButtons['end'].locked = true
        } else {
          if (this.isTimeStarted) {
            /**
             * this.isTimeStarted will be used during recording
             */

            this.sessionStartedButtonAttributes()
          } else {
            if (
              this.progress === 'live' &&
              this.executionType === 'recording'
            ) {
              if (
                PrePlayStore.sessionButtons['start'] &&
                (
                  PrePlayStore.sessionButtons['start'].text || ''
                ).toLowerCase() !== 'pause'
              ) {
                this.sessionResumeButtonAttributes()
              }
            } else if (
              this.progress === 'live' &&
              this.executionType !== 'recording'
            ) {
              this.sessionStartedButtonAttributes()
            }
          }
        }
      })
  }

  extractFromSportTypeOnly() {
    this.assignIdOnPredetermined()
      .then(next => {
        if (next) {
          return this.extractPreDetermined()
        }
      })
      .then(next => {
        if (next) {
          return this.extractBaseOptions()
        }
      })
      .then(next => {
        if (next) {
          return this.extractTimePeriods()
        }
      })
      .then(next => {
        if (next) {
          return this.extractInterruptPlays()
        }
      })
      .then(next => {
        if (next) {
          return this.extractMessagePlays()
        }
      })
      .then(next => {
        if (next) {
          return this.extractGmMesPlays()
        }
      })
      .then(next => {
        if (next) {
          return this.extractMesVidPlays()
        }
      })
  }

  _updateTeamScore_scheduled = {}
  updateTeamScore(team_idx, score) {
    if (
      PrePlayStore.teams &&
      PrePlayStore.teams[team_idx] &&
      PrePlayStore.teams[team_idx].score !== score
    ) {
      PrePlayStore.teams[team_idx].score = score
      if (this._updateTeamScore_scheduled[team_idx])
        clearTimeout(this._updateTeamScore_scheduled[team_idx])
      this._updateTeamScore_scheduled[team_idx] = setTimeout(() => {
        this._updateTeamScore_scheduled[team_idx] = null
        agent.GameServer.updateTeamScore(team_idx, score)
      }, 2000)
    }
  }

  updateTeamPossession(team_idx, possession) {
    if (PrePlayStore.teams && PrePlayStore.teams[team_idx]) {
      PrePlayStore.teams[team_idx].possession = possession ? 1 : 0
      agent.GameServer.updateTeamPossession(team_idx, possession ? 1 : 0)
    }
  }

  extractTeams() {
    return new Promise(resolve => {
      if (this.participants && this.participants.length > 0) {
        let teams = []
        this.participants.forEach((team, idx) => {
          teams.push({
            publicId: team.id,
            id: team.sequence,
            teamName: team.name,
            initial: team.initial,
            iconTopColor: team.topColor,
            iconBottomColor: team.bottomColor,
            index: idx,
            score: team.score || 0,
            possession: team.possession ? 1 : 0,
          })
        })

        PrePlayStore.setTeams(teams)
      }

      resolve(true)
    })
  }

  assignBlankPresetItems() {
    let id = PrePlayStore.presetItems.reduce((prev, curr) => {
      return prev.id > curr.id ? prev.id : curr.id
    })

    const blankPresets = [
      {
        id: 1,
        preset: '2-CHOICE',
        isMultiplier: false,
        question: '',
        choices: [{ value: '' }, { value: '' }],
        type: 'LivePlay',
        isBlank: true,
      },
      {
        id: 2,
        preset: '3-CHOICE',
        isMultiplier: false,
        question: '',
        choices: [{ value: '' }, { value: '' }, { value: '' }],
        type: 'LivePlay',
        isBlank: true,
      },
      {
        id: 3,
        preset: '4-CHOICE',
        isMultiplier: false,
        question: '',
        choices: [{ value: '' }, { value: '' }, { value: '' }, { value: '' }],
        type: 'LivePlay',
        isBlank: true,
      },
      {
        id: 4,
        preset: '5-CHOICE',
        isMultiplier: false,
        question: '',
        choices: [
          { value: '' },
          { value: '' },
          { value: '' },
          { value: '' },
          { value: '' },
        ],
        type: 'LivePlay',
        isBlank: true,
      },
    ]
    blankPresets.forEach(preset => {
      preset.id = ++id
      PrePlayStore.presetItems.push(preset)
    })
  }

  assignIdOnPredetermined() {
    PrePlayStore.resetPresetItems()

    return new Promise(resolve => {
      let id = 0

      for (let i = 0; i < this.preset.length; i++) {
        this.preset[i].id = ++id
      }

      for (let j = 0; j < this.baseOptions.length; j++) {
        this.baseOptions[j].id = ++id
      }

      resolve(true)
    })
  }

  assignIdOnPredeterminedORIG__() {
    PrePlayStore.resetPresetItems()

    return new Promise(resolve => {
      let id = 0

      if (!this.preset || (this.preset && this.preset.length < 1)) {
        id = 1000
        const blankPresets = [
          {
            id: 1,
            preset: '2-CHOICE',
            isMultiplier: false,
            question: '',
            choices: [{ value: '' }, { value: '' }],
            type: 'LivePlay',
          },
          {
            id: 2,
            preset: '3-CHOICE',
            isMultiplier: false,
            question: '',
            choices: [{ value: '' }, { value: '' }, { value: '' }],
            type: 'LivePlay',
          },
          {
            id: 3,
            preset: '4-CHOICE',
            isMultiplier: false,
            question: '',
            choices: [
              { value: '' },
              { value: '' },
              { value: '' },
              { value: '' },
            ],
            type: 'LivePlay',
          },
          {
            id: 4,
            preset: '5-CHOICE',
            isMultiplier: false,
            question: '',
            choices: [
              { value: '' },
              { value: '' },
              { value: '' },
              { value: '' },
              { value: '' },
            ],
            type: 'LivePlay',
          },
        ]
        blankPresets.forEach(preset => {
          preset.id = ++id
          PrePlayStore.presetItems.push(preset)
        })
      } else {
        for (let i = 0; i < this.preset.length; i++) {
          this.preset[i].id = ++id
        }

        for (let j = 0; j < this.baseOptions.length; j++) {
          this.baseOptions[j].id = ++id
        }

        // for (let k = 0; k < this.baseDefaults.length; k++) {
        //  this.baseDefaults[k].id = ++id
        // }
      }

      resolve(true)
    })
  }

  extractPreDetermined() {
    return new Promise(resolve => {
      if (this.preset && this.preset.length > 0) {
        this.preset.forEach(item => {
          let choices = []
          if (item.values && item.values.length > 0) {
            item.values.forEach(value => {
              let baseOptionOverride =
                item.base_options && item.base_options[value] !== undefined
                  ? item.base_options[value]
                  : null
              if (baseOptionOverride === '') {
                choices.push({ value: value })
                return
              }
              let lookupValue =
                baseOptionOverride !== null ? baseOptionOverride : value

              let valueToFind = this.baseOptions.filter(
                o =>
                  o.choice.trim().toLowerCase() ===
                  lookupValue.trim().toLowerCase()
              )[0]
              if (valueToFind) {
                choices.push({ value: value, nextId: valueToFind.id })
              } else {
                choices.push({ value: value })
              }
            })
          }

          let pre = {
            ...item,
            id: item.id,
            preset: item.name,
            question: item.question,
            choices: choices,
            type: item.type,
            base_options: item.base_options || {},
            awards: item.awards,
            price: item.price,
            bg_images: item.bg_images,
            time_limit: item.time_limit,
          }
          PrePlayStore.presetItems.push(pre)
        })
      }

      resolve(true)
    })
  }

  extractBaseOptions() {
    return new Promise(resolve => {
      const _baseOptions = JSON.parse(JSON.stringify(this.baseOptions))
      if (_baseOptions && _baseOptions.length > 0) {
        _baseOptions.forEach(item => {
          let choices = []
          if (item.values && item.values.length > 0) {
            try {
              item.values.forEach(value => {
                let valueToFind = this.baseOptions.filter(
                  o =>
                    o.choice.trim().toLowerCase() === value.trim().toLowerCase()
                )[0]
                if (valueToFind) {
                  choices.push({ value: value, nextId: valueToFind.id })
                } else {
                  choices.push({ value: value })
                }
              })
            } catch (err) {
              let defaultsToFind = this.baseDefaults.filter(
                o => o.name.trim().toLowerCase() === item.values
              )[0]
              if (defaultsToFind) {
                if (defaultsToFind.values && defaultsToFind.values.length > 0) {
                  defaultsToFind.values.forEach(value => {
                    choices.push({ value: value })
                  })
                }
              }
            }
          }

          let pre = {
            id: item.id,
            preset: 'multiplier',
            isMultiplier: true,
            question: item.question,
            choices: choices,
            type: 'LivePlay',
          }
          PrePlayStore.presetItems.push(pre)
        })
      }

      resolve(true)
    })
  }

  /* //tmp to delete
  addNonLivePlayOptions() {
    return new Promise(async resolve => {
      let id = 1000

      const nonLivePlayAB = {
        id: ++id,
        preset: 'A-B (Y/N)',
        question: '',
        choices: [{ value: 'yes' }, { value: 'no' }],
        readOnly: true,
        type: 'GameMaster, Sponsor, Prize',
      }
      //PrePlayStore.presetItems.push(nonLivePlayAB) //tmp to delete

      const nonLivePlayABTeams = {
        id: ++id,
        preset: 'A-B (TEAMS)',
        question: '',
        choices: [],
        readOnly: true,
        type: 'GameMaster, Sponsor, Prize',
      }
      //PrePlayStore.presetItems.push(nonLivePlayABTeams) //tmp to delete

      //this.assignBlankPresetItems() //tmp to delete

      resolve(true)
    })
  } */

  extractTimePeriods() {
    return new Promise(resolve => {
      if (this.timePeriods && this.timePeriods.length > 0) {
        let _tp = []
        this.timePeriods.forEach(t => {
          _tp.push(t)
        })

        _tp.unshift({ name: '' })
        PrePlayStore.setTimePeriods(_tp)
      }

      resolve(true)
    })
  }

  extractInterruptPlays() {
    return new Promise(resolve => {
      if (this.interruptPlays && this.interruptPlays.length > 0) {
        let _ip = []
        this.interruptPlays.forEach(i => {
          _ip.push(i)
        })

        PrePlayStore.setInterruptPlays(_ip)
      }

      resolve(true)
    })
  }

  extractMessagePlays() {
    return new Promise(resolve => {
      if (this.messagePlays && this.messagePlays.length > 0) {
        let _ip = []
        this.messagePlays.forEach(i => {
          _ip.push(i)
        })

        PrePlayStore.setMessagePlays(_ip)
      }

      resolve(true)
    })
  }

  extractGmMesPlays() {
    return new Promise(resolve => {
      if (this.gmMesPlays && this.gmMesPlays.length > 0) {
        let _ip = []
        this.gmMesPlays.forEach(i => {
          _ip.push(i)
        })
        PrePlayStore.setGmMesPlays(_ip)
      }

      resolve(true)
    })
  }

  extractMesVidPlays() {
    return new Promise(resolve => {
      if (this.mesVidPlays && this.mesVidPlays.length > 0) {
        let _ip = []
        this.mesVidPlays.forEach(i => {
          _ip.push(i)
        })
        PrePlayStore.setMesVidPlays(_ip)
      }

      resolve(true)
    })
  }

  extractPrePicks() {
    return new Promise(resolve => {
      if (this.prePicks && this.prePicks.length > 0) {
        this.prePicks.forEach(pp => {
          let _question =
            JSON.parse(
              JSON.parse(JSON.stringify(pp.questionHeader.replace(/'/g, '"')))
            ).value + ', '

          const _qDetail = JSON.parse(
            JSON.parse(JSON.stringify(pp.questionDetail.replace(/'/g, '"')))
          )
          _qDetail.forEach(qd => {
            _question += qd.value
          })

          pp.question = _question
          pp.choices =
            typeof pp.choices === 'string'
              ? JSON.parse(
                  JSON.parse(JSON.stringify(pp.choices.replace(/'/g, '"')))
                )
              : pp.choices

          if (pp.choiceType === 'ab') {
            for (let i = 0; i < pp.choices.length; i++) {
              pp.choices[i] = Object.assign(
                {},
                PrePlayStore.teams.filter(o => o.id === pp.choices[i].id)[0]
              )
            }
          }

          pp.forParticipant =
            typeof pp.forParticipant === 'string'
              ? JSON.parse(
                  JSON.parse(
                    JSON.stringify(pp.forParticipant.replace(/'/g, '"'))
                  )
                )
              : pp.forParticipant
          if (pp.forParticipant && Object.keys(pp.forParticipant).length > 0) {
            pp.forParticipant = Object.assign(
              {},
              PrePlayStore.teams.filter(o => o.id === pp.forParticipant.id)[0]
            )
          }

          if (pp.correctChoice && typeof pp.correctChoice === 'string')
            pp.correctChoice = JSON.parse(
              JSON.parse(JSON.stringify(pp.correctChoice.replace(/'/g, '"')))
            )
        })
      }
      this.incrementPrePickUpdateCounter()
    })
  }

  assetSetsData = null
  assetsAppConfigLocations = null

  extractSponsors() {
    return new Promise(async resolve => {
      //debugger
      if (
        this.gameId &&
        !(this.assetSetsData && this.assetsAppConfigLocations)
      ) {
        await this.assetsInit()
      }

      if (!this.sponsorPackages || !this.sponsorPackages.length)
        this.sponsorPackages = await agent.GameServer.readBrandPackages()
      const _sponsors = []
      for (let i = 0; i < this.sponsorPackages.length; i++) {
        const _raw = this.sponsorPackages[i]
        const _sponsor = _sponsors.filter(o => o.id === _raw.package_id)[0]
        const image_url_base =
          config.MEDIA_BASE_URL ||
          `${config.SECURE_PROTOCOL}://${config.IMAGE_URL}/${config.IMAGE_FOLDER}/`

        let subbrands = []
        if (this.assetSetsData) {
          const updatedObject = Object.entries(this.assetSetsData)
          let allSubbrands = []
          updatedObject.forEach(([assetSetId, a]) => {
            if (a && a.c) {
              const brands = Object.keys(a.c)
              if (
                brands &&
                brands.includes((_raw.sponsor_id || '').toString()) &&
                a.placement_subbrand_text
              ) {
                const parts = a.placement_subbrand_text
                  .split(',')
                  .map(s => s.trim())
                  .filter(Boolean)
                parts.forEach(part => {
                  allSubbrands.push({ text: part, assetSetId: assetSetId })
                })
              }
            }
          })
          // Remove duplicates based on text
          subbrands = allSubbrands.filter(
            (sb, idx, self) => idx === self.findIndex(t => t.text === sb.text)
          )
        }

        if (_sponsor) {
          if (_raw.sponsor_id) {
            _sponsor.brands.push({
              brandId: _raw.sponsor_id,
              brandName: _raw.sponsor_name,
              brandImage: `${image_url_base}${_raw.sponsor_image}`,
              brandExposureCount: _raw.sponsor_exposure_count,
              brandSortOrder: parseInt(_raw.sponsor_sort_order) || 0,
              subbrands: subbrands,
            })
          }
        } else {
          _sponsors.push({
            id: _raw.package_id,
            name: _raw.package_name,
            initial: _raw.package_initial,
            initialColor: _raw.package_initial_color,
            backgroundColor: _raw.package_background_color,
            circleBorderColor: _raw.package_circle_border_color,
            circleFill: _raw.package_circle_fill,
            brands: [
              {
                brandId: _raw.sponsor_id,
                brandName: _raw.sponsor_name,
                brandImage: `${image_url_base}${_raw.sponsor_image}`,
                brandExposureCount: _raw.sponsor_exposure_count,
                brandSortOrder: parseInt(_raw.sponsor_sort_order) || 0,
                subbrands: subbrands,
              },
            ],
          })
        }
      }

      // console.log("=== extractSponsors DEBUG ===");
      // console.log("Sponsor Packages length:", this.sponsorPackages ? this.sponsorPackages.length : 0);
      // console.log("Generated sponsors:", _sponsors);

      // try {
      //   const brandIdToName = {};
      //   const brandIdToSortOrder = {};
      //   _sponsors.forEach(cat => {
      //      if (cat.brands) {
      //         cat.brands.forEach(b => {
      //            brandIdToName[b.brandId] = b.brandName || b.name || "Unknown";
      //            brandIdToSortOrder[b.brandId] = b.brandSortOrder || 0;
      //         });
      //      }
      //   });

      //   const grouped = {}
      //   if (this.assetSetsData) {
      //     console.log("=== DEBUG: EXACT RAW DATA FOR 1562 FROM BACKEND ===", this.assetSetsData['1562']);
      //     Object.entries(this.assetSetsData).forEach(([assetSetIdKey, asset]) => {
      //       if (asset && asset.c) {
      //         Object.keys(asset.c).forEach(brandId => {
      //           if (!grouped[brandId]) grouped[brandId] = []
      //           grouped[brandId].push({
      //             assetsetId: assetSetIdKey,
      //             brandId: brandId,
      //             brandName: brandIdToName[brandId] || (asset.c[brandId].name) || 'Unknown',
      //             brandSortOrder: brandIdToSortOrder[brandId] !== undefined ? brandIdToSortOrder[brandId] : 0,
      //             assetSortOrder: asset.sortOrder || 0,
      //             subBrandText: asset.placement_subbrand_text,
      //             placement: asset.placement,
      //             events: asset.placement_events,
      //             locations: asset.locations
      //           })
      //         })
      //       }
      //     })
      //     const blob = new Blob([JSON.stringify(grouped, null, 2)], {type: 'application/json'})
      //     const url = URL.createObjectURL(blob)
      //     const a = document.createElement('a')
      //     a.href = url
      //     a.download = 'brand_assets_config.json'
      //     a.click()
      //     console.log("Downloaded brand_assets_config.json");
      //   }
      // } catch (err) {
      //   console.error("Failed to generate brand config JSON", err)
      // }

      PrePlayStore.sponsors = _sponsors
      return resolve(_sponsors)
    })
  }

  assetsInit() {
    return Promise.all([
      agent.GameServer.getBrandapp({ gameId: this.gameId }).then(
        assetSetsData => {
          if (assetSetsData && assetSetsData.asset_sets) {
            this.assetSetsData = assetSetsData.asset_sets
          }
        }
      ),
      agent.GameServer.getBrandappConfig(this.gameId).then(response => {
        if (response && response.locations)
          this.assetsAppConfigLocations = response.locations
      }),
    ])
  }

  isAssetAvailable(asset, loc, placement, moment) {
    const { assetsAppConfigLocations } = this
    if (!asset) return false
    if (!placement && moment) return false

    const assetPlacementList = asset.placement
      ? asset.placement
          .toLowerCase()
          .split('||')
          .map(p => p.trim())
          .filter(Boolean)
      : []
    const assetEventsRaw = asset.placement_events
      ? asset.placement_events
          .split(/\|\||,/)
          .map(e => e.toLowerCase().trim())
          .filter(Boolean)
      : []

    let locMatch = false
    if (!loc) {
      locMatch = true
    } else if (asset.locations) {
      asset.locations.forEach(location => {
        const assetPlayType =
          assetsAppConfigLocations &&
          assetsAppConfigLocations[location] &&
          assetsAppConfigLocations[location].play_type &&
          assetsAppConfigLocations[location].play_type[0]
        if (
          (assetPlayType || '').toLowerCase().includes(loc.toLowerCase()) ||
          location.toLowerCase() === loc.toLowerCase() ||
          location.toLowerCase().replace(/_/g, '') ===
            loc.toLowerCase().replace(/_/g, '') ||
          (loc.toLowerCase() === 'sponsor' &&
            location.toLowerCase() === 'sponsor_play')
        ) {
          locMatch = true
        }
      })
    }
    if (!locMatch) return false

    const placementArg = placement ? placement.toLowerCase().trim() : null
    const momentArg = moment ? moment.toLowerCase().trim() : null

    if (!placementArg) {
      if (assetPlacementList.length > 0) return false
      return true
    }

    if (!assetPlacementList.includes(placementArg)) return false

    const allowedMomentsByPlacement = {}
    for (const ev of assetEventsRaw) {
      const colonIdx = ev.indexOf(':')
      if (colonIdx > -1) {
        const p = ev.substring(0, colonIdx).trim()
        const m = ev.substring(colonIdx + 1).trim()
        if (!allowedMomentsByPlacement[p]) allowedMomentsByPlacement[p] = []
        allowedMomentsByPlacement[p].push(m)
      } else {
        if (assetPlacementList.length === 1) {
          const p = assetPlacementList[0]
          if (!allowedMomentsByPlacement[p]) allowedMomentsByPlacement[p] = []
          allowedMomentsByPlacement[p].push(ev)
        }
      }
    }

    const requiredMomentsForThisPlacement =
      allowedMomentsByPlacement[placementArg] || []

    if (momentArg) {
      if (requiredMomentsForThisPlacement.length === 0) return false
      if (!requiredMomentsForThisPlacement.includes(momentArg)) return false
    } else {
      if (requiredMomentsForThisPlacement.length > 0) return false
    }

    return true
  }

  isBrandAvailable(brandId, loc, placement, moment) {
    const { assetSetsData } = this
    if (brandId && assetSetsData) {
      const updatedObject = Object.values(assetSetsData)
      const filteredObjects = _.filter(updatedObject, function(asset_set) {
        if (asset_set && asset_set.c) {
          const brands = Object.keys(asset_set.c)
          return brands && brands.includes(brandId.toString())
        }
      })
      if (!filteredObjects || filteredObjects.length === 0) return false

      return filteredObjects.some(asset =>
        this.isAssetAvailable(asset, loc, placement, moment)
      )
    }
    return false
  }

  getMatchingAssetSetForSubBrand(brandId, subBrand, loc, placement, moment) {
    const { assetSetsData } = this
    if (!brandId || !assetSetsData || !subBrand) return null

    const subBrandText = typeof subBrand === 'object' ? subBrand.text : subBrand
    const subBrandAssetSetId =
      typeof subBrand === 'object' ? subBrand.assetSetId : null

    if (subBrandAssetSetId) {
      const asset =
        assetSetsData[subBrandAssetSetId] ||
        assetSetsData[String(subBrandAssetSetId)]
      if (
        asset &&
        asset.c &&
        Object.keys(asset.c).includes(String(brandId)) &&
        this.isAssetAvailable(asset, loc, placement, moment)
      ) {
        return {
          assetSetId: subBrandAssetSetId,
          text: subBrandText || asset.placement_subbrand_text,
        }
      }
    }

    if (subBrandText) {
      const updatedObject = Object.entries(assetSetsData)
      for (const [assetSetId, asset] of updatedObject) {
        if (!asset || !asset.c) continue
        const brands = Object.keys(asset.c)
        if (!brands.includes(String(brandId))) continue
        if (!asset.placement_subbrand_text) continue
        const parts = asset.placement_subbrand_text
          .split(',')
          .map(s => s.trim().toLowerCase())
        if (
          parts.includes(subBrandText.toLowerCase().trim()) &&
          this.isAssetAvailable(asset, loc, placement, moment)
        ) {
          return {
            assetSetId: assetSetId,
            text: subBrandText,
          }
        }
      }
    }

    return null
  }

  isSubBrandAvailable(brandId, subBrand, loc, placement, moment) {
    return Boolean(
      this.getMatchingAssetSetForSubBrand(
        brandId,
        subBrand,
        loc,
        placement,
        moment
      )
    )
  }

  getAssetSetSubBrandData(assetSetId) {
    const { assetSetsData } = this
    if (assetSetId && assetSetsData) {
      const assetSet =
        assetSetsData[assetSetId] || assetSetsData[assetSetId.toString()]
      if (assetSet) {
        return {
          placement_subbrand_text: assetSet.placement_subbrand_text,
          placement_subbrand_add: assetSet.placement_subbrand_add,
          assetSetId: assetSetId,
        }
      }

      const updatedObject = Object.values(assetSetsData)
      const foundAssetSet = updatedObject.find(a => {
        if (
          a.id == assetSetId ||
          a.asset_set_id == assetSetId ||
          a.assetSetId == assetSetId ||
          a.package_id == assetSetId
        )
          return true
        for (let key in a) {
          if (a[key] == assetSetId) return true
        }
        return false
      })
      if (foundAssetSet) {
        return {
          placement_subbrand_text: foundAssetSet.placement_subbrand_text,
          placement_subbrand_add: foundAssetSet.placement_subbrand_add,
          assetSetId: assetSetId,
        }
      }
    }
    return null
  }

  @observable
  gameMasters = null

  @action
  async setGameMasters() {
    // await agent.GameServer.readGameMasters({ gameId: this.gameId }).then(
    //   gameMastersData => {
    //     this.gameMasters = gameMastersData
    //   }
    // )
    this.gameMasters =
      this.gmetadata && this.gmetadata.public
        ? this.gmetadata.public.gameGameMasters || []
        : []
  }

  sessionResumeButtonAttributes() {
    PrePlayStore.sessionButtons['start'] = {
      backgroundColor: '#008001',
      color: '#fff',
      text: 'resume session',
      locked: false,
    }
    PrePlayStore.sessionButtons['end'].locked = true
    this.setSessionMode(0)
  }

  sessionPauseButtonAttributes() {
    PrePlayStore.sessionButtons['start'] = {
      locked: this.executionType === 'recording' ? false : true,
      backgroundColor: '#000000',
      color: '#c61818',
      text: 'recording' === this.executionType ? 'pause' : 'On Air',
    }
    PrePlayStore.sessionButtons['end'].locked = false
    this.setSessionMode(1)
  }

  sessionStartedButtonAttributes() {
    PrePlayStore.sessionButtons['start'] = {
      backgroundColor: '#000000',
      color: '#c61818',
      text: this.executionType === 'recording' ? 'pause' : 'On Air',
      locked: this.executionType === 'recording' ? false : true,
    }
    PrePlayStore.sessionButtons['end'].locked = false
    this.setSessionMode(1)
  }

  addThePlay(playItem, isReused) {
    let play = null
    if ('announce' === playItem.type.trim().toLowerCase()) {
      const replaceWords = '<span class="ql-cursor">﻿</span>'
      let header = playItem.announcements
        .filter(o => o.area === 'header')[0]
        .value.replace(replaceWords, '')
        .replace('<span class="ql-cursor">?</span>', '')
      let middle = playItem.announcements
        .filter(o => o.area === 'middle')[0]
        .value.replace(replaceWords, '')
        .replace('<span class="ql-cursor">?</span>', '')
      let bottom = playItem.announcements
        .filter(o => o.area === 'bottom')[0]
        .value.replace(replaceWords, '')
        .replace('<span class="ql-cursor">?</span>', '')

      play = {
        preset_n_id: playItem.preset_n_id,
        pmetadata: playItem.pmetadata,
        id: playItem.id,
        index: playItem.id,
        type: 'Announce',
        subType: PrePlayStore.subType,
        sponsor: playItem.sponsor,
        sponsorId:
          playItem.sponsor &&
          playItem.sponsor.sponsorItem &&
          playItem.sponsor.sponsorItem.brandId
            ? playItem.sponsor.sponsorItem.brandId
            : 0,
        announcements: [
          { area: 'header', value: header },
          { area: 'middle', value: middle },
          { area: 'bottom', value: bottom },
        ],
        executionType: playItem.executionType,
        recordedAutomation: playItem.recordedAutomation,
        editorEvents: playItem.editorEvents,
        backgroundImage: playItem.backgroundImage,
      }
    } else {
      //delete playItem.choices
      play = JSON.parse(JSON.stringify(playItem))
      delete play.index
      delete play.length

      //////////--OLD play.participantId = play.team.publicId
      play.participantId =
        play.team && play.team.publicId
          ? play.team.publicId
          : play.participantId
      //delete Object.assign(play, {['forParticipant']: play['team']})['team'];
      delete play.team

      play.choices =
        play.choices &&
        play.choices.map(_ch => {
          if (_ch.value) _ch.value = this.processChoiceTplField(_ch.value)
          return _ch
        })
      if (play.multiplierChoices && play.multiplierChoices.length > 0) {
        const _first_question_index = play.multiplierChoices.findIndex(
          o => o.id === play.id
        )
        if (_first_question_index > 0)
          play.multiplierChoices.unshift(
            play.multiplierChoices.splice(_first_question_index, 1)[0]
          )
        play.multiplierChoices[0].question = play.playTitle.value
        play.multiplierChoices[0].choices = play.choices
        for (let i = 0; i < play.multiplierChoices.length; i++) {
          //if (play.multiplierChoices[i].isBlank) {
          //  play.multiplierChoices[i].choices = play.choices
          //} else { //tmp to delete
          play.multiplierChoices[i].choices = play.multiplierChoices[
            i
          ].choices.map(_ch => {
            if (_ch.value) _ch.value = this.processChoiceTplField(_ch.value)
            return _ch
          })
          //} //tmp to delete
        }
      }
      play.playTitle = play.playTitle.value
      //+mod by aurelio play.presetId = play.preset.id
      ///////////--OLD
      //play.presetId = play.preset.preset
      play.presetId =
        play.preset && play.preset.preset ? play.preset.preset : play.presetId
      delete play.preset
      // if (playItem.multiplierChoices && playItem.multiplierChoices.length > 0) {
      //   playItem.multiplierChoices.forEach(item => {
      //     delete item.isMultiplier
      //     delete item.locked
      //     delete item.type
      //   })
      // }
      play.award =
        play.award && Object.keys(play.award).length > 0
          ? play.award.value
          : null
      if (play.sponsor && play.sponsor.sponsorItem) {
        const sItem = play.sponsor.sponsorItem
        play.pmetadata = play.pmetadata || {}
        play.pmetadata.public = play.pmetadata.public || {}
        play.pmetadata.public.isManualOverride = true
        play.pmetadata.public.isAutomatic = false
        play.pmetadata.isAutomatic = false
        if (sItem.brandId) {
          play.pmetadata.public.resolvedSponsorId = sItem.brandId
          play.pmetadata.public.resolvedBrandId = sItem.brandId
        }
        if (sItem.assetSetId) {
          play.pmetadata.public.resolvedAssetSetId = sItem.assetSetId
        }
        if (sItem.selectedSubbrand || sItem.placement_subbrand_text) {
          play.pmetadata.public.placement_subbrand_text =
            sItem.selectedSubbrand || sItem.placement_subbrand_text
        }
      }
      play.sponsorId =
        play.sponsor &&
        play.sponsor.sponsorItem &&
        play.sponsor.sponsorItem.brandId
          ? play.sponsor.sponsorItem.brandId
          : 0
      play.assetSetId =
        play.sponsor &&
        play.sponsor.sponsorItem &&
        play.sponsor.sponsorItem.assetSetId
          ? play.sponsor.sponsorItem.assetSetId
          : 0
      delete play.sponsor
    }
    if (isReused && play) {
      play.pmetadata = play.pmetadata || {}
      play.pmetadata.reUsedId = this.updateReUseUniqueId()
    }
    agent.GameServer.addThePlay(play)
    if (isReused) this.isreusedPlaySuccess(playItem && playItem.id)
  }

  importQA(args) {
    return agent.GameServer.importQA(args)
      .then(response => {
        if (response && response.data && response.data.data)
          return Promise.resolve(response.data.data.playCount)
      })
      .catch(err => {
        return Promise.reject(err)
      })
  }

  @observable
  reusedPlay = null
  @action
  isreusedPlaySuccess(id) {
    if (id) this.reusedPlay = id
  }

  @observable
  isReusedPlayIndex = -1

  @action
  setIsReusedPlayIndex(data) {
    this.isReusedPlayIndex = _.findIndex(data, item => {
      if (item && item.pmetadata && item.pmetadata.reUsedId) {
        return item.pmetadata.reUsedId === this.reusedUniqueId
      }
    })
  }

  @observable
  reusedUniqueId = null

  updateReUseUniqueId() {
    this.reusedUniqueId = uniqueId()
    return this.reusedUniqueId
  }

  updatePlay(playItem) {
    if ('announce' === playItem.type.trim().toLowerCase()) {
      playItem.playTitle = ''
    }
    const playItemToUpdate = Object.assign({}, playItem)
    delete playItemToUpdate.choices
    delete playItemToUpdate.index
    delete playItemToUpdate.length
    //delete Object.assign(playItemToUpdate, {['forParticipant']: playItemToUpdate['team']})['team'];

    playItemToUpdate.participantId = playItemToUpdate.team
      ? playItemToUpdate.team.publicId || 0
      : 0
    delete playItemToUpdate.team
    playItemToUpdate.playTitle =
      playItemToUpdate.playTitle &&
      typeof playItemToUpdate.playTitle === 'object'
        ? playItemToUpdate.playTitle.value
        : playItemToUpdate.playTitle || null
    playItemToUpdate.award =
      playItemToUpdate.award && Object.keys(playItemToUpdate.award).length > 0
        ? playItemToUpdate.award.value
        : null

    // old
    // playItemToUpdate.sponsorId = playItemToUpdate.sponsor
    //   ? playItemToUpdate.sponsor.id
    //   : 0;

    if (playItemToUpdate.sponsor && playItemToUpdate.sponsor.sponsorItem) {
      const sItem = playItemToUpdate.sponsor.sponsorItem
      playItemToUpdate.pmetadata = playItemToUpdate.pmetadata || {}
      if (typeof playItemToUpdate.pmetadata === 'string') {
        try {
          playItemToUpdate.pmetadata = JSON.parse(playItemToUpdate.pmetadata)
        } catch (e) {
          playItemToUpdate.pmetadata = {}
        }
      }
      playItemToUpdate.pmetadata = playItemToUpdate.pmetadata || {}
      playItemToUpdate.pmetadata.public =
        playItemToUpdate.pmetadata.public || {}
      playItemToUpdate.pmetadata.public.isManualOverride = true
      playItemToUpdate.pmetadata.public.isAutomatic = false
      playItemToUpdate.pmetadata.isManualOverride = true
      playItemToUpdate.pmetadata.isAutomatic = false
      if (sItem.brandId) {
        playItemToUpdate.pmetadata.public.resolvedSponsorId = sItem.brandId
        playItemToUpdate.pmetadata.public.resolvedBrandId = sItem.brandId
        playItemToUpdate.pmetadata.resolvedSponsorId = sItem.brandId
        playItemToUpdate.pmetadata.resolvedBrandId = sItem.brandId
        playItemToUpdate.resolvedSponsorId = sItem.brandId
        playItemToUpdate.resolvedBrandId = sItem.brandId
      }
      if (sItem.assetSetId) {
        playItemToUpdate.pmetadata.public.resolvedAssetSetId = sItem.assetSetId
        playItemToUpdate.pmetadata.resolvedAssetSetId = sItem.assetSetId
        playItemToUpdate.resolvedAssetSetId = sItem.assetSetId
      }
      if (sItem.selectedSubbrand || sItem.placement_subbrand_text) {
        playItemToUpdate.pmetadata.public.placement_subbrand_text =
          sItem.selectedSubbrand || sItem.placement_subbrand_text
        playItemToUpdate.pmetadata.placement_subbrand_text =
          sItem.selectedSubbrand || sItem.placement_subbrand_text
      }
    }

    let resolvedBrandMetadata = playItemToUpdate.pmetadata
    if (typeof resolvedBrandMetadata === 'string') {
      try {
        resolvedBrandMetadata = JSON.parse(resolvedBrandMetadata)
      } catch (e) {
        resolvedBrandMetadata = {}
      }
    }
    resolvedBrandMetadata = resolvedBrandMetadata || {}
    const resolvedPublicMetadata =
      resolvedBrandMetadata.public || resolvedBrandMetadata
    const nestedSponsorId =
      playItemToUpdate.sponsor &&
      playItemToUpdate.sponsor.sponsorItem &&
      playItemToUpdate.sponsor.sponsorItem.brandId
    const nestedAssetSetId =
      playItemToUpdate.sponsor &&
      playItemToUpdate.sponsor.sponsorItem &&
      playItemToUpdate.sponsor.sponsorItem.assetSetId

    playItemToUpdate.sponsorId =
      nestedSponsorId ||
      playItemToUpdate.sponsorId ||
      resolvedPublicMetadata.resolvedSponsorId ||
      playItemToUpdate.resolvedSponsorId ||
      0

    playItemToUpdate.assetSetId =
      nestedAssetSetId ||
      playItemToUpdate.assetSetId ||
      resolvedPublicMetadata.resolvedAssetSetId ||
      playItemToUpdate.resolvedAssetSetId ||
      0

    let isManualOverride =
      playItemToUpdate.pmetadata &&
      playItemToUpdate.pmetadata.public &&
      typeof playItemToUpdate.pmetadata.public.isManualOverride !== 'undefined'
        ? playItemToUpdate.pmetadata.public.isManualOverride
        : null

    if (isManualOverride === false) {
      playItemToUpdate.sponsorId = 0
      playItemToUpdate.assetSetId = 0
      delete playItemToUpdate.resolvedSponsorId
      delete playItemToUpdate.resolvedBrandId
      delete playItemToUpdate.resolvedAssetSetId
      playItemToUpdate.pmetadata = playItemToUpdate.pmetadata || {}
      playItemToUpdate.pmetadata.isManualOverride = false
      playItemToUpdate.pmetadata.isAutomatic = true
      playItemToUpdate.pmetadata.brandMode = 'automatic'
      delete playItemToUpdate.pmetadata.resolvedSponsorId
      delete playItemToUpdate.pmetadata.resolvedBrandId
      delete playItemToUpdate.pmetadata.resolvedAssetSetId
      delete playItemToUpdate.pmetadata.placement_subbrand_text
      if (playItemToUpdate.pmetadata.public) {
        playItemToUpdate.pmetadata.public.isManualOverride = false
        playItemToUpdate.pmetadata.public.isAutomatic = true
        delete playItemToUpdate.pmetadata.public.resolvedSponsorId
        delete playItemToUpdate.pmetadata.public.resolvedBrandId
        delete playItemToUpdate.pmetadata.public.resolvedAssetSetId
        delete playItemToUpdate.pmetadata.public.placement_subbrand_text
      }
    }

    if (!playItemToUpdate.sponsorId) {
      playItemToUpdate.pmetadata = playItemToUpdate.pmetadata || {}
      if (isManualOverride !== false) {
        playItemToUpdate.pmetadata.brandMode =
          isManualOverride === true ? 'default' : 'automatic'
      }
    }

    delete playItemToUpdate.sponsor

    agent.GameServer.updatePlay(playItemToUpdate)
    agent.GameServer.gamesUpdate({
      entityType: 'Play',
      value: playItemToUpdate,
    })
  }

  removePlay(args) {
    agent.GameServer.removePlay({
      gameId: this.gameId,
      id: args.id,
      executionType: args.executionType,
    })
  }

  setPlayCurrent(data) {
    console.log('CURRENT\n', data)
    if (data) {
      if ('announce' === data.type.toLowerCase()) {
        //id, index, choies, playTitle, stars
        data.index = data.id
        data.choices = []
        data.sponsorExpanded = false
        data.sessionStarted = this.progress === 'live'

        // new sponsor
        data.sponsor = this.getSelectedSponsor(data.sponsorId, data)

        if (
          !PrePlayStore.currentPlayItem ||
          isPlayDataDifferent(PrePlayStore.currentPlayItem, data)
        ) {
          PrePlayStore.setCurrentPlayItem(data)
        }
        return
      }

      data = this.preprocessItem(data)

      if (PrePlayStore.currentPlayItem) {
        if (isPlayDataDifferent(PrePlayStore.currentPlayItem, data)) {
          PrePlayStore.setCurrentPlayItem(data)
        }
      } else {
        PrePlayStore.setCurrentPlayItem(data)
      }
    } else {
      PrePlayStore.setCurrentPlayItem(null)
    }
  }

  setPlayNext(data) {
    console.log('NEXT\n', data)
    if (data) {
      if ('announce' === data.type.toLowerCase()) {
        //id, index, choies, playTitle, stars
        data.index = data.id
        data.choices = []
        data.sponsorExpanded = false
        data.sessionStarted = this.progress === 'live'

        // new sponsor
        data.sponsor = this.getSelectedSponsor(data.sponsorId, data)

        if (
          !PrePlayStore.nextPlayItem ||
          isPlayDataDifferent(PrePlayStore.nextPlayItem, data)
        ) {
          PrePlayStore.setNextPlayItem(data)
        }
        return
      }

      data = this.preprocessItem(data, true)

      if (PrePlayStore.nextPlayItem) {
        if (isPlayDataDifferent(PrePlayStore.nextPlayItem, data)) {
          PrePlayStore.setNextPlayItem(data)
        }
      } else {
        PrePlayStore.setNextPlayItem(data)
      }
    } else {
      PrePlayStore.setNextPlayItem(null)
    }
  }

  setUpdatedPlay(item) {
    const id = item.id || item.gamePlayId
    const existing =
      PrePlayStore.preplayItems.find(o => (o.id || o.gamePlayId) == id) ||
      (PrePlayStore.currentPlayItem &&
      (PrePlayStore.currentPlayItem.id ||
        PrePlayStore.currentPlayItem.gamePlayId) == id
        ? PrePlayStore.currentPlayItem
        : null) ||
      (PrePlayStore.nextPlayItem &&
      (PrePlayStore.nextPlayItem.id || PrePlayStore.nextPlayItem.gamePlayId) ==
        id
        ? PrePlayStore.nextPlayItem
        : null)

    if (existing) {
      if (!item.multiplierChoices || !item.multiplierChoices.length) {
        item.multiplierChoices = existing.multiplierChoices
      }
      if (!item.choices || !item.choices.length) {
        item.choices = existing.choices
      }
      if (!item.preset || !Object.keys(item.preset).length) {
        item.preset = existing.preset
      }
      if (!item.team) {
        item.team = existing.team
      }
    }

    item = this.preprocessItem(item, true)
    PrePlayStore.setUpdatedPlay(item)
  }

  setPlayStack(data) {
    console.log('setAllPlays STACK length', data.length) //tmp

    if (data && data.length > 0) {
      for (let i = 0; i < data.length; i++) {
        let item = data[i]
        if ('announce' === item.type.toLowerCase()) {
          //id, index, choies, playTitle, stars
          item.index = item.id
          item.choices = []
          item.sponsorExpanded = false
          item.sessionStarted = this.progress === 'live'

          // new sponsor
          item.sponsor = this.getSelectedSponsor(item.sponsorId, item)
        } else {
          data[i] = this.preprocessItem(data[i], true)
        }
      }
    }
    // if (
    //   data &&
    //   data.length === 0 &&
    //   PrePlayStore.nextPlayItem &&
    //   PrePlayStore.currentPlayItem &&
    //   PrePlayStore.nextPlayItem.gamePlayId ===
    //     PrePlayStore.currentPlayItem.gamePlayId &&
    //   PrePlayStore.nextPlayItem.sequence ===
    //     PrePlayStore.currentPlayItem.sequence
    // )
    //   PrePlayStore.setNextPlayItem(null)
    PrePlayStore.setPrePlayItems(data)
    this.setIsReusedPlayIndex(data)
    PrePlayStore.setIsAddingStack(false)
    console.log('setAllPlays STACK done') //tmp
    return Promise.resolve(null)
  }

  setPlayUnresolved(data) {
    const list = []
    if (data && data.length > 0) {
      for (let i = 0; i < data.length; i++) {
        let item = data[i]
        if ('announce' !== item.type.toLowerCase()) {
          item = this.preprocessItem(item)
          list.push(item)
        }
      }
    }

    console.log('UNRESOLVED\n', data)
    PrePlayStore.setUnresolvedItems(list)
    return Promise.resolve(null)
  }

  @action
  setPrePickResolved(data) {
    if (
      !this.gameId ||
      !data.prePickId ||
      !data.correctChoice ||
      !data.gameId ||
      data.gameId !== this.gameId
    )
      return
    let prePick = this.prePicks.filter(_p => _p.prePickId === data.prePickId)[0]
    if (prePick) {
      if (typeof data.correctChoice === 'string') {
        try {
          data.correctChoice = JSON.parse(
            JSON.parse(JSON.stringify(data.correctChoice.replace(/'/g, '"')))
          )
        } catch (_e) {
          data.correctChoice = null
        }
      }
      if (data.correctChoice) prePick.correctChoice = data.correctChoice
      this.incrementPrePickUpdateCounter()
    }
  }

  setPlayResolved(data) {
    const list = []
    if (data && data.length > 0) {
      for (let i = 0; i < data.length; i++) {
        let item = data[i]
        if ('announce' === item.type.toLowerCase()) {
          item.index = data.id
          item.choices = []
          item.sponsorExpanded = false
          item.sessionStarted = false

          // new sponsor
          item.sponsor = this.getSelectedSponsor(item.sponsorId, item)
        } else {
          item = this.preprocessItem(item)
        }

        list.push(item)
      }
    }

    console.log('setPlayResolved\n', list)
    this.resolvedPlays = list
    this.setMesAssetsCount()
    PrePlayStore.setResolvedItems(list)
    return Promise.resolve(null)
  }

  gameStart() {
    agent.GameServer.gameStart({
      gameId: this.gameId,
      executionType: this.executionType,
      headless: this.headless,
    })
    this.setSessionMode(1)
  }

  gameEnd() {
    agent.GameServer.gameEnd({
      gameId: this.gameId,
      stage: 'recording' === this.executionType ? 'pending' : 'postgame',
      executionType: this.executionType,
    })
    this.setSessionMode(0)
  }

  processChoiceTplField(choice) {
    const field = choice.match(/^\{(\w+)\}$/)
    if (!field) return choice
    const field_match = field[1].match(/^player|team(\d+)$/i)
    return field_match && PrePlayStore.teams.length >= field_match[1]
      ? PrePlayStore.teams[field_match[1] - 1].teamName
      : choice
  }

  goPlay(playItem) {
    let play = null
    if (playItem) {
      if ('announce' === playItem.type.trim().toLowerCase()) {
        const replaceWords = '<span class="ql-cursor">﻿</span>'
        let header = playItem.announcements
          .filter(o => o.area === 'header')[0]
          .value.replace(replaceWords, '')
          .replace('<span class="ql-cursor">?</span>', '')
        let middle = playItem.announcements
          .filter(o => o.area === 'middle')[0]
          .value.replace(replaceWords, '')
          .replace('<span class="ql-cursor">?</span>', '')
        let bottom = playItem.announcements
          .filter(o => o.area === 'bottom')[0]
          .value.replace(replaceWords, '')
          .replace('<span class="ql-cursor">?</span>', '')

        if (!playItem.id) {
          const _announceSponsorId =
            playItem.sponsor &&
            playItem.sponsor.sponsorItem &&
            playItem.sponsor.sponsorItem.brandId
              ? playItem.sponsor.sponsorItem.brandId
              : 0
          const _announcePmetadata = playItem.pmetadata || {}
          let _isAnnounceManualOverride =
            _announcePmetadata.public &&
            typeof _announcePmetadata.public.isManualOverride !== 'undefined'
              ? _announcePmetadata.public.isManualOverride
              : null

          let finalAnnounceSponsorId = _announceSponsorId
          if (_isAnnounceManualOverride === false) {
            finalAnnounceSponsorId = 0
            _announcePmetadata.brandMode = 'automatic'
          }

          if (!finalAnnounceSponsorId && _isAnnounceManualOverride !== false) {
            _announcePmetadata.brandMode =
              _isAnnounceManualOverride === true ? 'default' : 'automatic'
          }
          play = {
            id: '',
            index: '',
            gameId: this.gameId,
            type: 'Announce',
            sponsor: playItem.sponsor,
            sponsorId: finalAnnounceSponsorId,
            isNew: playItem.isNew,
            announcements: [
              { area: 'header', value: header },
              { area: 'middle', value: middle },
              { area: 'bottom', value: bottom },
            ],
            recordedAutomation: playItem.recordedAutomation,
            editorEvents: playItem.editorEvents,
            executionType: playItem.executionType,
            pmetadata: _announcePmetadata,
          }
          if (playItem.subType) play.subType = playItem.subType
        } else {
          play = { ...playItem }
          play.executionType = this.executionType
          play.gameId = this.gameId
          play.sponsor = playItem.sponsor
          play.sponsorId =
            playItem.sponsor &&
            playItem.sponsor.sponsorItem &&
            playItem.sponsor.sponsorItem.brandId
              ? playItem.sponsor.sponsorItem.brandId
              : 0

          let isManualOverride =
            play.pmetadata &&
            play.pmetadata.public &&
            typeof play.pmetadata.public.isManualOverride !== 'undefined'
              ? play.pmetadata.public.isManualOverride
              : null
          if (isManualOverride === false) {
            play.sponsorId = 0
            play.pmetadata = play.pmetadata || {}
            play.pmetadata.brandMode = 'automatic'
          }

          if (!play.sponsorId) {
            play.pmetadata = play.pmetadata || {}
            if (isManualOverride !== false) {
              play.pmetadata.brandMode =
                isManualOverride === true ? 'default' : 'automatic'
            }
          }
          play.announcements = [
            { area: 'header', value: header },
            { area: 'middle', value: middle },
            { area: 'bottom', value: bottom },
          ]
          if (playItem.subType) play.subType = playItem.subType
        }
      } else {
        play = JSON.parse(JSON.stringify(playItem))
        if (playItem.subType) play.subType = playItem.subType
        delete play.index
        delete play.length

        play.gameId = this.gameId
        play.executionType = this.executionType

        play.participantId =
          play.team && play.team.publicId
            ? play.team.publicId
            : play.participantId
        delete play.team

        play.choices =
          play.choices &&
          play.choices.map(_ch => {
            if (_ch.value) _ch.value = this.processChoiceTplField(_ch.value)
            return _ch
          })
        if (play.multiplierChoices && play.multiplierChoices.length > 0) {
          play.multiplierChoices[0].question = play.playTitle.value
          play.multiplierChoices[0].choices = play.choices
          for (let i = 0; i < play.multiplierChoices.length; i++) {
            //if (play.multiplierChoices[i].isBlank) {
            //  play.multiplierChoices[i].choices = play.choices
            //} else { //tmp to delete
            play.multiplierChoices[i].choices = play.multiplierChoices[
              i
            ].choices.map(_ch => {
              if (_ch.value) _ch.value = this.processChoiceTplField(_ch.value)
              return _ch
            })
            //} //tmp to delete
          }
        }
        play.playTitle = play.playTitle.value
        play.presetId =
          play.preset && play.preset.preset ? play.preset.preset : play.presetId
        delete play.preset
        play.award =
          play.award && Object.keys(play.award).length > 0
            ? play.award.value
            : null
        let resolvedBrandMetadata = play.pmetadata
        if (typeof resolvedBrandMetadata === 'string') {
          try {
            resolvedBrandMetadata = JSON.parse(resolvedBrandMetadata)
          } catch (e) {
            resolvedBrandMetadata = {}
          }
        }
        resolvedBrandMetadata = resolvedBrandMetadata || {}
        const resolvedPublicMetadata =
          resolvedBrandMetadata.public || resolvedBrandMetadata
        const nestedSponsorId =
          play.sponsor &&
          play.sponsor.sponsorItem &&
          play.sponsor.sponsorItem.brandId
        const nestedAssetSetId =
          play.sponsor &&
          play.sponsor.sponsorItem &&
          play.sponsor.sponsorItem.assetSetId

        play.sponsorId =
          nestedSponsorId ||
          resolvedPublicMetadata.resolvedSponsorId ||
          play.resolvedSponsorId ||
          0

        play.assetSetId =
          nestedAssetSetId ||
          resolvedPublicMetadata.resolvedAssetSetId ||
          play.resolvedAssetSetId ||
          0

        let isManualOverride =
          play.pmetadata &&
          play.pmetadata.public &&
          typeof play.pmetadata.public.isManualOverride !== 'undefined'
            ? play.pmetadata.public.isManualOverride
            : null
        if (isManualOverride === false) {
          play.sponsorId = 0
          play.assetSetId = 0
          play.pmetadata = play.pmetadata || {}
          play.pmetadata.brandMode = 'automatic'
        }

        if (!play.sponsorId) {
          play.pmetadata = play.pmetadata || {}
          if (isManualOverride !== false) {
            play.pmetadata.brandMode =
              isManualOverride === true ? 'default' : 'automatic'
          }
        }
        delete play.sponsor
      }

      console.log('GO PLAY', play)
      agent.GameServer.goPlay(play)
    }
  }

  resolvePlay(args) {
    agent.GameServer.resolvePlay(args)
  }

  endCurrentPlay(args) {
    agent.GameServer.endCurrentPlay(args)
  }

  resetDatabase() {
    agent.GameServer.resetDatabase(this.gameId)
    PrePlayStore.resetValues()
  }

  getGames(args) {
    return agent.GameServer.getGames(args)
  }

  resolvePrePick(args) {
    return agent.GameServer.resolvePrePick(args)
  }

  @observable
  announcementValueObservable = null
  @action
  setAnnouncementValueObservable(val) {
    this.announcementValueObservable = val
  }

  gameResume() {
    this.sessionPauseButtonAttributes()
    agent.GameServer.gameResume({
      gameId: this.gameId,
      lastSessionTime: this.lastSessionTime,
    })
    return Promise.resolve(true)
  }

  gameResumeRespond(next) {
    if (next) {
      this.progress = 'live'
      this.sessionPauseButtonAttributes()
    }
  }

  gamePause() {
    this.sessionResumeButtonAttributes()
    agent.GameServer.gamePause({ gameId: this.gameId })
  }

  gamePauseRespond(next) {
    if (next) {
      this.sessionResumeButtonAttributes()
    }
  }

  @computed
  get disableHeader() {
    return (
      this.dateEndSession ||
      this.progress === 'postgame' ||
      this.progress === 'end' ||
      (this.recordedPlays &&
        Array.isArray(this.recordedPlays) &&
        this.recordedPlays.length > 2 &&
        'recording' === this.executionType) ||
      PrePlayStore.sessionButtons['start'].text.includes('resume') ||
      this.isRecordEnded ||
      this.executionType === 'automation'
    )
  }

  isLeap = false
  @action
  setLeap(val) {
    this.isLeap = val
  }

  executionType = 'normal'
  @action
  setExecutionType(val) {
    this.executionType = val
  }

  isViewRecordedEvent = false
  @action
  setViewRecordedEvent(val) {
    this.isViewRecordedEvent = val
  }

  isRecordEnded = false
  @action
  setRecordEnded(val) {
    this.isRecordEnded = val
  }

  operator = null
  @action
  setOperator(val) {
    this.operator = val
  }

  headless = false
  @action
  setHeadless(val) {
    this.headless = val
  }

  @observable
  sessionMode = 0
  @action
  setSessionMode(val) {
    this.sessionMode = val
  }

  sportType = null
  @action
  setSportType(val) {
    this.sportType = val
  }

  subSportGenre = null
  @action
  setSubSportGenre(val) {
    this.subSportGenre = val
  }

  @action
  setSequences(val) {
    this.HCommLastHeaderSequence = val.headerPlaySequence
    this.HCommLastPlaySequence = val.playSequence
    this.lastSequence = val.sequence
  }

  @observable
  syncSessionStartInitResponded = null

  @observable
  syncSessionStartFinalResponded = null

  @observable
  syncSessionStartCancelResponded = null

  @observable
  syncSessionCreatePlayResponded = null
  @action
  setSyncSessionCreatePlayResponded(val) {
    this.syncSessionCreatePlayResponded = val
  }

  @observable
  syncCreateSessionResponded = null
  @action
  setSyncCreateSessionResponded(val) {
    this.syncCreateSessionResponded = val
  }

  @observable
  syncCreateInterruptionResponded = null
  @action
  setSyncCreateInterruptionResponded(val) {
    this.syncCreateInterruptionResponded = val
  }

  @observable
  syncHeaderTeamResponded = null
  @action
  setSyncHeaderTeamResponded(val) {
    this.syncHeaderTeamResponded = val
  }

  @observable
  syncInput = null

  @observable
  syncDD = null

  @observable
  syncCreatePlayStar = null
  @action
  setSyncCreatePlayStar(val) {
    this.syncCreatePlayStar = val
  }

  @action
  resetSyncCreatePlay() {
    this.syncSessionCreatePlayResponded = null
    this.syncCreateSessionResponded = null
    this.syncCreateInterruptionResponded = null
  }

  @observable
  playStats = []
  @action
  addPlayStats(data, all) {
    console.log('addPlayStats', data) //tmp
    if (data && !all && this.isGAEnabled) {
      let connected =
        data.played + data.played_tmp > data.connected
          ? data.played + data.played_tmp
          : data.connected
      let missed = connected - data.played - data.played_tmp
      let correct = connected
        ? connected - missed
        : data
        ? data.won + data.won_tmp
        : 0
      let play = PrePlayStore.resolvedItems.find(
        e => e.gamePlayId === data.gameplay_id
      )
      let incorrect =
        play && play.result && play.result.correctAnswers
          ? connected * play.result.correctAnswers.length -
            data.won -
            data.won_tmp
          : connected - data.won - data.won_tmp
      Play_Stats(
        data.gameplay_id,
        data.type,
        correct,
        incorrect,
        missed,
        data.spent,
        data.earned.t,
        data.earned.p,
        data.earned.s
      )
    }
    if (all && this.playStats.length)
      this.playStats.splice(0, this.playStats.length)
    Array.isArray(data)
      ? this.playStats.push(...data)
      : this.playStats.push(data)
  }

  @observable
  brandappStats = []
  @action
  setBrandappStats(data) {
    console.log('setBrandappStats', data) //tmp
    this.brandappStats.splice(0, this.brandappStats.length)
    this.brandappStats.push(...data)
  }

  syncAssemblePlay(item) {
    agent.GameServer.syncAssemblePlay(item)
  }

  syncRequest(params) {
    params.gameId = this.gameId
    params.operator = this.operator
    agent.GameServer.syncRequest(params)
  }

  syncResponse(data) {
    if (!data) {
      return
    }
    console.log('SYNC RESPONSE', data)
    if (localStorage.getItem('gameid') === data.gameId) {
      switch (data.processName) {
        case 'START_INIT':
          this.syncSessionStartInitResponded = data
          break
        case 'START_FINAL':
          this.syncSessionStartFinalResponded = data
          break
        case 'START_CANCEL':
          this.syncSessionStartCancelResponded = data
          break
        case 'CREATE_PLAY':
          this.syncSessionCreatePlayResponded = data
          break
        case 'CREATE_SESSION':
          this.syncCreateSessionResponded = data
          break
        case 'CREATE_INTERRUPTION':
          this.syncCreateInterruptionResponded = data
          break
        case 'ADD_PLAY':
          if (!data.isReuse) {
            //PrePlayStore.setAddToStackItem(null)
            this.resetSyncCreatePlay()
          }
          break
        case 'HEADER_TEAM_CHANGE':
          this.syncHeaderTeamResponded = data
          break
        case 'INPUT_CHANGE':
          if (this.operator !== data.operator) {
            this.syncInput = data
          }
          break
        case 'DD_CHANGE':
          this.syncDD = data
          break
        case 'DD_CHANGE_STAR':
          this.syncCreatePlayStar = data
      }
    }
  }

  @computed
  get isSessionStarted() {
    return (
      (this.executionType === 'recording' &&
        this.progress === 'live' &&
        !this.isViewRecordedEvent &&
        this.sessionMode === 1) ||
      (this.executionType !== 'recording' && this.progress === 'live')
    )
  }

  @computed
  get isLockedForAllExecutionType() {
    return 'recording,automation'.match(new RegExp(this.executionType, 'gi'))
      ? 'live' === this.progress
        ? !this.isSessionStarted
        : false
      : false
  }

  getSelectedSponsor(_sponsorId, playItem) {
    const _sponsor = { sponsorCategory: null, sponsorItem: null }
    _sponsor.sponsorCategory = PrePlayStore.sponsors.filter(cat => {
      return cat.brands.filter(brand => {
        return brand.brandId === _sponsorId
      })[0]
    })[0]

    let baseSponsorItem =
      _sponsor.sponsorCategory && _sponsor.sponsorCategory.brands
        ? _sponsor.sponsorCategory.brands.filter(
            o => o.brandId === _sponsorId
          )[0]
        : null

    if (baseSponsorItem) {
      let _assetSetId = null
      let pmeta = playItem && playItem.pmetadata
      if (typeof pmeta === 'string') {
        try {
          pmeta = JSON.parse(pmeta)
        } catch (e) {}
      }
      const pmetaPublic = pmeta && pmeta.public ? pmeta.public : pmeta
      _assetSetId =
        (pmetaPublic &&
          (pmetaPublic.resolvedAssetSetId || pmetaPublic.assetSetId)) ||
        (pmeta && (pmeta.resolvedAssetSetId || pmeta.assetSetId)) ||
        (playItem && (playItem.assetSetId || playItem.resolvedAssetSetId)) ||
        null

      let subBrandData = null
      if (_assetSetId) {
        subBrandData = this.getAssetSetSubBrandData(_assetSetId)
      }

      if (
        !subBrandData &&
        baseSponsorItem.subbrands &&
        baseSponsorItem.subbrands.length > 0
      ) {
        const subBrandText =
          (pmetaPublic && pmetaPublic.placement_subbrand_text) ||
          (pmeta && pmeta.placement_subbrand_text)
        if (subBrandText) {
          const matched = baseSponsorItem.subbrands.find(
            sb => sb.text === subBrandText
          )
          if (matched) {
            subBrandData = this.getAssetSetSubBrandData(matched.assetSetId)
          }
        }
      }

      if (subBrandData) {
        _sponsor.sponsorItem = { ...baseSponsorItem, ...subBrandData }
      } else {
        _sponsor.sponsorItem = { ...baseSponsorItem }
      }
    }

    return _sponsor
  }

  @observable
  gameAssets = {}

  gameAssetCategories = []

  @action
  setGameAssets(data) {
    /*
    data is the Object with 3 keys: game, sport_type, categories

    data.categories is a 2-level structure with all top level and 2nd level categories that are assigned to any of the assets (from game and sport_type keys).

    Categories that none of the assets in this data are assigned to are not included.

    data.categories is an Array of Objects with the following structure:
          {
            "top_category_id": 5,
            "category": "Top Category Name",
            "categories": [ //list of Level 2 categories under this top category
              {
                "gm_id": 14, //!!! category_ids[] in asset data refer to this gm_id
                "category": "L2 Category Name",
                "parent_category_id": 5, //same as top_category_id above
                "count": 2 //number of videos in this L2 category
              },
              //...
            ],
            "count": 2 //number of videos in this top category
          }
    Asset data has a key: category_ids. It is an Array with L2 gm_id values, refering to L2 categories assigned to the asset.
    */
    if (data.categories) {
      this.gameAssetCategories = data.categories
      delete data.categories
    }
    this.gameAssets = data
  }

  @observable
  servedAssetsCounts = null

  @action
  setMesAssetsCount() {
    const gmAnnounceItems = _.filter(this.resolvedPlays, play => {
      return play && (play.subType || '').toLowerCase().includes('gm_announce_')
    })
    const ranMesAssets = _.map(gmAnnounceItems, item => {
      let assetInfo =
        item &&
        item.announcements &&
        item.announcements[2] &&
        typeof item.announcements[2].value === 'string'
          ? item.announcements[2].value
          : null
      try {
        return JSON.parse(assetInfo.match(/(?:\{.*?\})(?=\s*<p>)/)[0])
      } catch (error) {
        return null
      }
    })

    this.servedAssetsCounts = ranMesAssets.reduce(
      (acc, { gm_id, id, messageSequence }) => {
        // Skip entries with undefined gm_id, id, or messageSequence
        if (
          gm_id === undefined ||
          id === undefined ||
          messageSequence === undefined
        ) {
          return acc
        }

        // Initialize the gm_id if it does not exist
        if (!acc[gm_id]) {
          acc[gm_id] = {
            totalCount: 0, // Initialize totalCount for gm_id
          }
        }

        // Initialize the id within the gm_id if it does not exist
        if (!acc[gm_id][id]) {
          acc[gm_id][id] = {
            totalCount: 0, // To store total count of messages for this id
          }
        }

        // Initialize the count for this messageSequence if it does not exist
        if (!acc[gm_id][id][messageSequence]) {
          acc[gm_id][id][messageSequence] = 0
        }

        // Increment the count for this messageSequence
        acc[gm_id][id][messageSequence] += 1

        // Increment the total count for this id
        acc[gm_id][id].totalCount += 1

        // Increment the total count for this gm_id
        acc[gm_id].totalCount += 1

        return acc
      },
      {}
    )
  }

  @action
  updateGmAssetQuantity(gmAssetId, newQuantity) {
    // return agent.GameServer.updateGmAssetQuantity(gmAssetId, newQuantity)
  }

  @observable
  qaFileList = null
  @action
  readQAFileList() {
    const sportType = this.sportType && this.sportType.code
    return agent.GameServer.readQAFileList(sportType).then(data => {
      this.qaFileList = data && data.data && data.data.data
      return Promise.resolve(this.qaFileList || [])
    })
  }

  @observable
  qaFile = null
  @action
  readQAFile(fileId) {
    return agent.GameServer.readQAFile(fileId)
  }

  resetVars() {
    this.isLoading = false
    this.isError = false
    this.isAvailable = false
    this.gameId = null
    this.progress = 'active'
    this.dateEndSession = null
    this.progressStates = null
    this.participants = []
    this.preset = []
    this.baseOptions = []
    this.baseDefaults = []
    this.timePeriods = []
    this.interruptPlays = []
    this.messagePlays = []
    this.venue = null
    this.prePicks = []
    this.recordedPlays = []
    this.lastSessionTime = 0
    this.videoPath = null
    this.isTimeStarted = false
    this.sponsorPackages = []
    this.sportTypes = []
    this.lastSequence = 0
    this.HCommLastHeaderSequence = 0
    this.HCommLastPlaySequence = 0
    this.HCommLastWait = 0
    this.automationRestartedServerCallback = false
    this.hasReset = false
    this.announcementValueObservable = null
    this.isLeap = false
    this.isViewRecordedEvent = false
    this.isRecordEnded = false
    this.sessionMode = 0

    // COMMENTED OUT FOR RESET RECORD GAME EVENT.
    // this.sportType = null
    // this.subSportGenre = null

    this.syncSessionStartInitResponded = null
    this.syncSessionStartFinalResponded = null
    this.syncSessionStartCancelResponded = null
  }
}

module.exports = new GameStore()



// WEBPACK FOOTER //
// src/stores/GameStore.js